import { useEffect, useState } from "react";
import type { ErrorType } from "../types/interfaces";

const useErrorMessage = ({
	isError,
	error,
}: { error: ErrorType | null; isError: boolean }) => {
	const [errorMsg, setErrorMsg] = useState<string>("");

	useEffect(() => {
		setErrorMsg(extractErrorMessage(error));
	}, [error, isError]);

	return { errorMsg };
};

// Utility function to recursively traverse nested error objects
const traverseNestedError = (obj: any): any => {
	if (!obj || typeof obj !== "object") return obj;
	
	// Common nested error properties to check
	const errorProperties = ['error', 'data', 'detail', 'message'];
	
	for (const prop of errorProperties) {
		if (prop in obj && typeof obj[prop] === "object" && obj[prop] !== null) {
			return traverseNestedError(obj[prop]);
		}
	}
	
	// Return the current object if no nested error found
	return obj;
};

// Check if a string contains HTML content
const isHtmlContent = (str: string): boolean => {
	if (!str || typeof str !== "string") return false;
	
	// Check for common HTML indicators
	const htmlPatterns = [
		/<!DOCTYPE/i,
		/<html/i,
		/<head/i,
		/<body/i,
		/<div/i,
		/<script/i,
		/<style/i,
		/<meta/i,
	];
	
	return htmlPatterns.some(pattern => pattern.test(str));
};

// Sanitize and validate error message
const sanitizeErrorMessage = (message: string): string => {
	if (!message || typeof message !== "string") {
		return "Something went wrong";
	}
	
	// Reject HTML content
	if (isHtmlContent(message)) {
		console.error("HTML error response received from backend:", message.substring(0, 200));
		return "An unexpected error occurred. Please try again or contact support.";
	}
	
	// Limit message length to prevent UI overflow
	const MAX_LENGTH = 500;
	if (message.length > MAX_LENGTH) {
		console.warn("Error message too long, truncating:", message.substring(0, 100));
		return message.substring(0, MAX_LENGTH) + "...";
	}
	
	return message;
};

// Helper function to extract message from various error formats
const extractMessage = (errMsg: any): string => {
	if (typeof errMsg === "string") return sanitizeErrorMessage(errMsg);
	if (!errMsg || typeof errMsg !== "object") return "Something went wrong";

	// Priority order for error message extraction
	const messageExtractors = [
		() => typeof errMsg.details === "string" ? errMsg.details : null,
		() => Array.isArray(errMsg.details) ? errMsg.details[0] : null,
		() => typeof errMsg.error === "string" ? errMsg.error : null,
		() => Array.isArray(errMsg.non_field_errors) ? errMsg.non_field_errors[0] : null,
		() => typeof errMsg.message === "string" ? errMsg.message : null,
		() => {
			if ("email" in errMsg) {
				return typeof errMsg.email === "string" 
					? errMsg.email 
					: Array.isArray(errMsg.email) ? errMsg.email[0] : null;
			}
			return null;
		}
	];

	for (const extractor of messageExtractors) {
		const message = extractor();
		if (message) return sanitizeErrorMessage(message);
	}

	return "Something went wrong";
};

export const extractErrorMessage = (error: ErrorType | null): string => {
	if (!error || typeof error !== "object") return "";

	if ("status" in error) {
		// Handle FetchBaseQueryError
		const nestedError = traverseNestedError(error.data || error);
		return extractMessage(nestedError);
	} else {
		// Handle SerializedError
		return error.message || "Something went wrong";
	}
}

export default useErrorMessage;